import React from 'react';

interface TestResult {
  passed: boolean;
  status?: 'passed' | 'failed' | 'not_required';
  details: string;
  qualifyingPeriods?: string[];
}

interface CausationTestProps {
  vTest: TestResult;
  modifiedVTest: TestResult;
  downOnlyTest: TestResult;
  benchmarkPeriod?: string;
}

export default function CausationTest({ vTest, modifiedVTest, downOnlyTest, benchmarkPeriod }: CausationTestProps) {
  // Determine if Modified V-Test should show as "NOT REQUIRED"
  // If V-Test passed, the backup test wasn't needed
  const getEffectiveStatus = (test: TestResult, isBackup: boolean, primaryPassed: boolean): 'passed' | 'failed' | 'not_required' => {
    if (test.status) return test.status;
    if (isBackup && primaryPassed && !test.passed) return 'not_required';
    return test.passed ? 'passed' : 'failed';
  };

  const modifiedVTestStatus = getEffectiveStatus(modifiedVTest, true, vTest.passed);

  const TestResultCard = ({ name, test, icon, status }: { name: string; test: TestResult; icon: string; status: 'passed' | 'failed' | 'not_required' }) => {
    const styles = {
      passed: {
        bg: 'bg-green-50 border-green-200',
        icon: 'text-green-600',
        text: 'text-green-800',
        badge: 'bg-green-200 text-green-800',
        label: 'PASSED'
      },
      failed: {
        bg: 'bg-red-50 border-red-200',
        icon: 'text-red-600',
        text: 'text-red-800',
        badge: 'bg-red-200 text-red-800',
        label: 'FAILED'
      },
      not_required: {
        bg: 'bg-gray-50 border-gray-200',
        icon: 'text-gray-400',
        text: 'text-gray-600',
        badge: 'bg-gray-200 text-gray-600',
        label: 'NOT REQUIRED'
      }
    };

    const style = styles[status];

    return (
      <div className={`border-2 rounded-lg p-4 ${style.bg}`}>
        <div className="flex items-start gap-3">
          <div className={`text-2xl ${style.icon}`}>{icon}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h4 className={`font-semibold ${status === 'not_required' ? 'text-gray-600' : 'text-gray-900'}`}>{name}</h4>
              <span className={`px-2 py-1 rounded text-xs font-bold ${style.badge}`}>
                {style.label}
              </span>
            </div>
            <p className={`text-sm ${style.text} mb-2`}>
              {status === 'not_required' ? 'V-Test passed. Backup test not evaluated.' : test.details}
            </p>
            {status !== 'not_required' && test.qualifyingPeriods && test.qualifyingPeriods.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-semibold text-gray-700 mb-1">Qualifying Periods:</p>
                <div className="flex flex-wrap gap-1">
                  {test.qualifyingPeriods.map((period: string, idx: number) => (
                    <span key={idx} className="bg-white px-2 py-1 rounded text-xs font-medium text-gray-700 border">
                      {period}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Causation Analysis</h3>
        {benchmarkPeriod && (
          <p className="text-sm text-gray-600 mt-1">
            Benchmark Period: <span className="font-medium text-gray-900">{benchmarkPeriod}</span>
          </p>
        )}
      </div>

      <div className="space-y-3">
        <TestResultCard name="V-Test (Standard)" test={vTest} icon="✓" status={vTest.passed ? 'passed' : 'failed'} />
        <TestResultCard name="Modified V-Test" test={modifiedVTest} icon="◇" status={modifiedVTestStatus} />
        <TestResultCard name="Down-Only Test" test={downOnlyTest} icon="↓" status={downOnlyTest.passed ? 'passed' : 'failed'} />
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">What This Means</h4>
        <p className="text-sm text-blue-800">
          The business {vTest.passed ? 'passed' : 'failed'} the V-Test, which is the primary test for causation. 
          {vTest.passed && ' This confirms that revenue losses during the coverage period were significantly different from baseline patterns, supporting the claim for BI compensation.'}
          {!vTest.passed && ' Additional analysis may be needed to establish causation for the claim.'}
        </p>
      </div>
    </div>
  );
}
